import React, { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@Sage/hoi-poi-ui';
import { PRODUCT_MODE_CODES } from 'constants/Constants';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import AbsoluteLoader from 'components/AbsoluteLoader';
import AbsoluteError from 'components/AbsoluteError';
import Context from 'managers/Context';
import * as ConfigActions from 'actions/Config';
import * as ServerListActions from 'actions/ServerList';
import SettingsLayout from '../components/SettingsLayout';
import Content from './Content';
import EnvironmentHeader from './components/environment';
import './styles.scss';

const MAX_DECIMALS_ALLOWED = 10;
const DECIMALS_FIELD_IDS = [
    'intdecimalscurrencygeneral',
    'intdecimalscurrencyprices',
    'intdecimalsquantity',
    'intdecimalsgeneral',
];

const CompanyManagement = memo(() => {
    const containerRef = useRef(null);
    const dispatch = useDispatch();
    const userEnvironmentId = useSelector((state) => state.config?.userData?.idEntorno);
    const productModeCode = useSelector((state) => state.config?.productModeCode);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [schema] = useState(() => Context.settingsManager.getCompanySettingsSchema());
    const [model, setModel] = useState(null);
    const [selectedEnvironment, setSelectedEnvironment] = useState(null);
    const [defaultEnvironment, setDefaultEnvironmentState] = useState(null);
    const [loadingSave, setLoadingSave] = useState(false);
    const [modified, setModified] = useState(false);
    const [isToggled, setIsToggled] = useState(false);
    const [environments, setEnvironments] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});

    const isSageImplementation = useMemo(
        () => productModeCode?.includes(PRODUCT_MODE_CODES.SAGE),
        [productModeCode],
    );

    const contentSchema = useMemo(
        () =>
            schema.map((section) => ({
                ...section,
                tabFields: section.tabFields.map((field) => ({
                    ...field,
                    readOnly:
                        Boolean(field.readOnly) ||
                        (isSageImplementation && DECIMALS_FIELD_IDS.includes(field.id)),
                })),
            })),
        [isSageImplementation, schema],
    );

    const getDecimalsFieldError = useCallback((fieldId, value) => {
        if (!DECIMALS_FIELD_IDS.includes(fieldId)) return '';

        if (value === '' || value === null || value === undefined) return '';

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return '';

        return numericValue > MAX_DECIMALS_ALLOWED
            ? getLiteralWithParameters('label_max_decimals_allowed', [MAX_DECIMALS_ALLOWED])
            : '';
    }, []);

    const hasFieldErrors = useMemo(
        () => Object.values(fieldErrors).some((error) => Boolean(error)),
        [fieldErrors],
    );

    const syncCurrentEnvironmentConfig = useCallback(
        (companySettings) => {
            const state = Context.store.getState();
            const currencies = state?.serverList?.tblCurrency?.data;
            const idCurrency = companySettings.idcurrency;
            const units = companySettings.units;
            const currency =
                Array.isArray(currencies) &&
                currencies.find((c) => String(c.value) === String(idCurrency));

            const userDataPatch = {
                blnImperialLengthSystem: String(units ?? 0),
                currencyId: idCurrency,
                currencyISO: currency?.striso4217code,
                currencySymbol: currency?.strsymbol,
                lengthSystem: Number(units) === 1 ? 'imperial' : 'metric',
            };

            dispatch(ConfigActions.updateUserDataConfiguration(userDataPatch));

            if (Context.config?.userData) {
                Context.setConfig({
                    ...Context.config,
                    userData: {
                        ...Context.config.userData,
                        ...userDataPatch,
                    },
                });
            }
        },
        [dispatch],
    );

    const getCompanySettings = useCallback(
        (idEnvironment, envList) => {
            const finalEnvironments = envList ?? environments;
            Context.settingsManager.getCompanySettingsEnvironment(
                idEnvironment,
                finalEnvironments,
                (data) => {
                    setLoading(false);
                    setError(false);
                    setModel(data);
                    setFieldErrors({});
                    setSelectedEnvironment(idEnvironment);
                    setIsToggled(false);
                    setEnvironments(finalEnvironments);
                },
                () => {
                    setLoading(false);
                    setError(true);
                    setModel(null);
                },
            );
        },
        [environments],
    );

    const refreshEnvironmentState = useCallback(
        (idEnvironment) => {
            dispatch(ServerListActions.removeList('tblSucursales'));
            return dispatch(ServerListActions.getList('tblSucursales')).then((list) => {
                let refreshedDefaultEnvironment = list.find((el) => el['isdefault'] === 'True');
                if (!refreshedDefaultEnvironment) refreshedDefaultEnvironment = list[0];
                if (refreshedDefaultEnvironment?.['-id']) {
                    setDefaultEnvironmentState(refreshedDefaultEnvironment['-id']);
                }
                getCompanySettings(idEnvironment, list);
            });
        },
        [dispatch, getCompanySettings],
    );

    useEffect(() => {
        const loadData = () => {
            new Promise((resolve, reject) => {
                Context.settingsManager.getTblSucursales(resolve, reject);
            }).then(
                (list) => {
                    let defaultEnv = list.find((el) => el['isdefault'] === 'True');
                    if (!defaultEnv) defaultEnv = list[0];
                    if (!defaultEnv?.['-id']) return;

                    setDefaultEnvironmentState(defaultEnv['-id']);
                    getCompanySettings(defaultEnv['-id'], list);
                },
                () => {
                    setLoading(false);
                    setError(true);
                },
            );
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Clone the class instance preserving its prototype (getters/setters)
    const cloneModel = useCallback((prev) => {
        const next = Object.create(Object.getPrototypeOf(prev));
        next.entity = { ...prev.entity };
        return next;
    }, []);

    const onChange = useCallback(
        (field, value) => {
            const nextError = getDecimalsFieldError(field, value);
            setFieldErrors((prev) => ({
                ...prev,
                [field]: nextError,
            }));

            let schemaField = null;
            schema.forEach((section) => {
                section.tabFields.forEach((f) => {
                    if (f.id === field) schemaField = f;
                });
            });

            setModel((prev) => {
                const next = cloneModel(prev);
                if (schemaField?.dataType === 'singleValueList') {
                    const idKey = typeof field.id === 'object' ? field.id.id : field.id;
                    const descKey = typeof field.id === 'object' ? field.id.description : undefined;
                    next[idKey] = value.id;
                    if (descKey) next[descKey] = value.description;
                } else {
                    next[field] = value;
                }
                return next;
            });
            setModified(true);
        },
        [schema, cloneModel, getDecimalsFieldError],
    );

    const onUpdate = useCallback(() => {
        if (hasFieldErrors) return;

        setLoadingSave(true);

        const next = cloneModel(model);
        if (isToggled) {
            next.isDefaultEnvironment = 1;
        } else if (selectedEnvironment !== defaultEnvironment) {
            next.isDefaultEnvironment = 0;
        }

        setModel(next);

        Context.settingsManager.setCompanySettingsEnvironment(
            selectedEnvironment,
            next,
            () => {
                const isCurrentEnvironment =
                    userEnvironmentId != null &&
                    String(selectedEnvironment) === String(userEnvironmentId);

                successToast({
                    title: getLiteral('succes_data_saved'),
                    text: getLiteral('succes_entityupdatedsuccessfully'),
                });

                if (isCurrentEnvironment) {
                    syncCurrentEnvironmentConfig(next);
                }

                refreshEnvironmentState(selectedEnvironment).finally(() => {
                    containerRef.current?.scrollIntoView();
                    setModified(false);
                    setLoadingSave(false);
                    setIsToggled(false);
                    if (isToggled) setDefaultEnvironmentState(selectedEnvironment);
                });
            },
            () => {
                errorToast({ text: getLiteral('error_an_error_occurred') });
                containerRef.current?.scrollIntoView();
                setLoadingSave(false);
            },
        );
    }, [
        isToggled,
        selectedEnvironment,
        defaultEnvironment,
        model,
        cloneModel,
        hasFieldErrors,
        userEnvironmentId,
        syncCurrentEnvironmentConfig,
        refreshEnvironmentState,
    ]);

    const switchEnvironment = useCallback(
        (field, id) => {
            getCompanySettings(id ? id.id : '-1');
        },
        [getCompanySettings],
    );

    const handleToggle = useCallback(() => {
        setIsToggled((prev) => !prev);
        setModified(true);
    }, []);

    const renderButton =
        !loading && !loadingSave ? (
            <Button
                onClick={modified && !hasFieldErrors ? onUpdate : undefined}
                isDisabled={!modified || hasFieldErrors}
            >
                {getLiteral('action_save')}
            </Button>
        ) : (
            <Button isLoading isDisabled>
                {getLiteral('action_save')}
            </Button>
        );

    return (
        <div ref={containerRef} className="fm-company-management">
            <SettingsLayout
                title={getLiteral('title_my_account')}
                description={getLiteral('helptext_my_account')}
            >
                <div className="fm-company-management__content">
                    {error ? (
                        <AbsoluteError subtitle={getLiteral('error_an_error_occurred')} />
                    ) : loading ? (
                        <AbsoluteLoader size="massive" />
                    ) : (
                        <>
                            <EnvironmentHeader
                                model={model}
                                schema={schema}
                                defaultEnvironment={defaultEnvironment}
                                selectedEnvironment={selectedEnvironment}
                                onChange={switchEnvironment}
                                handleToggle={handleToggle}
                                isToggled={isToggled}
                            />
                            <Content
                                model={model}
                                schema={contentSchema}
                                onChange={onChange}
                                fieldErrors={fieldErrors}
                            />
                            <div className="fm-company-management__button">{renderButton}</div>
                        </>
                    )}
                </div>
            </SettingsLayout>
        </div>
    );
});

export default CompanyManagement;
